import { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Paper, Tabs, Tab, TextField, Button,
  MenuItem, Alert, Chip, Card, CardContent, LinearProgress, IconButton,
  Skeleton,
} from '@mui/material';
import {
  Add, Star, People, CalendarMonth, AttachMoney, Delete,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import masterclassService, { MasterClassEvent, CreateMasterClassData } from '../../services/masterclassService';
import eventsService from '../../services/eventsService';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { colors } from '../../theme/theme';

const statusChip = (s: string) => {
  const map: Record<string, { color: string; bg: string }> = {
    draft: { color: colors.warning.dark, bg: colors.warning.subtle },
    open: { color: colors.success.dark, bg: colors.success.subtle },
    confirmed: { color: colors.primary.dark, bg: colors.primary.light },
    completed: { color: colors.neutral[600], bg: colors.neutral[100] },
    cancelled: { color: '#c62828', bg: '#ffebee' },
  };
  // eslint-disable-next-line security/detect-object-injection -- map is a local literal Record with default fallback to map.draft.
  const st = map[s] || map.draft;
  return <Chip label={s} size="small" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, textTransform: 'capitalize' }} />;
};

export default function PremiumDashboardPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [events, setEvents] = useState<MasterClassEvent[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [confirmDlg, setConfirmDlg] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => { /* no-op */ } });

  // Create form state
  const [form, setForm] = useState<CreateMasterClassData>({
    title: '', description: '', topic_brief: '', zone_id: '',
    scheduled_date: '', scheduled_time: '18:00',
    duration_minutes: 90, masterclass_fee: 10, voucher_tier: 'standard',
    min_capacity: 5, max_capacity: 6,
  });

  useEffect(() => {
    Promise.all([
      masterclassService.getMyEvents().catch(() => []),
      eventsService.getZones().catch(() => []),
    ]).then(([evts, zns]) => {
      setEvents(evts);
      setZones(zns);
      setLoading(false);
    });
  }, []);

  const handleCreate = async () => {
    if (!form.title || !form.description || !form.zone_id || !form.scheduled_date) {
      setError('Please fill in all required fields'); return;
    }
    setCreating(true); setError(''); setSuccess('');
    try {
      const result = await masterclassService.create(form);
      setSuccess(result.message || 'Master class submitted for approval!');
      setForm({ ...form, title: '', description: '', topic_brief: '' });
      // Refresh events
      const evts = await masterclassService.getMyEvents().catch(() => []);
      setEvents(evts);
      setTab(0); // Switch to My Events
    } catch (e: any) {
      console.warn('[PremiumDashboardPage] create master class failed', e);
      setError(e.message || 'Failed to create master class');
    } finally { setCreating(false); }
  };

  const handleCancel = (eventId: string) => {
    setConfirmDlg({
      open: true,
      title: 'Cancel Master Class',
      message: 'Cancel this master class? All attendees will be refunded.',
      onConfirm: async () => {
        setConfirmDlg(d => ({ ...d, open: false }));
        try {
          await masterclassService.cancel(eventId);
          setEvents(events.filter(e => e.id !== eventId));
          setSuccess('Master class cancelled.');
        } catch (e: any) { console.warn('[PremiumDashboardPage] cancel master class failed', e); setError(e.message); }
      },
    });
  };

  if (loading) return <Container maxWidth="md" sx={{ py: 4 }}><Skeleton variant="rounded" height={400} /></Container>;

  const totalRevenue = events.reduce((sum, e) => sum + (e.revenue_estimate || 0), 0);
  const totalAttendees = events.reduce((sum, e) => sum + (e.attendee_count || 0), 0);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Star sx={{ color: colors.primary.main, fontSize: 32 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{t('pages.premium.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage your master classes
          </Typography>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'My Events', value: events.length },
          { label: 'Total Attendees', value: totalAttendees },
          { label: 'Revenue (AZN)', value: totalRevenue.toFixed(0) },
        ].map((s, i) => (
          <Paper key={i} sx={{ flex: 1, p: 2, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary.main }}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`My Events (${events.length})`} />
          <Tab label="Create New" icon={<Add />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: My Events */}
      {tab === 0 && (
        <Box>
          {events.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>No master classes yet</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setTab(1)}>
                Create Your First Master Class
              </Button>
            </Paper>
          ) : events.map(e => (
            <Card key={e.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{e.title}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {statusChip(e.status)}
                      <Chip label={`${e.masterclass_fee} AZN fee`} size="small" icon={<AttachMoney sx={{ fontSize: 14 }} />} />
                      <Chip label={e.voucher_tier} size="small" sx={{ textTransform: 'capitalize' }} />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarMonth sx={{ fontSize: 16, color: colors.neutral[400] }} />
                      <Typography variant="body2">{e.scheduled_date}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <People sx={{ fontSize: 16, color: colors.neutral[400] }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.attendee_count}/{e.max_capacity}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Capacity bar */}
                <LinearProgress variant="determinate"
                  value={e.max_capacity > 0 ? (e.attendee_count / e.max_capacity) * 100 : 0}
                  sx={{ borderRadius: 2, height: 6, bgcolor: colors.neutral[100], my: 1,
                    '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main, borderRadius: 2 } }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Revenue: <strong>{e.revenue_estimate} AZN</strong>
                    {e.avg_rating ? ` · Rating: ${e.avg_rating.toFixed(1)}★` : ''}
                  </Typography>
                  {(e.status === 'draft' || e.status === 'open') && (
                    <IconButton size="small" color="error" aria-label="Cancel event" onClick={() => handleCancel(e.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Tab 1: Create New */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Create a Master Class</Typography>

          <TextField fullWidth label="Title *" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="e.g., Introduction to UX Research Methods"
            sx={{ mb: 2 }} />

          <TextField fullWidth multiline rows={3} label="Description *" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="What will participants learn? What makes this class special?"
            sx={{ mb: 2 }} />

          <TextField fullWidth multiline rows={2} label="Topic Brief (shown to attendees)" value={form.topic_brief}
            onChange={e => setForm({ ...form, topic_brief: e.target.value })}
            sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField select fullWidth label="Zone *" value={form.zone_id}
              onChange={e => setForm({ ...form, zone_id: e.target.value })}>
              {zones.map((z: any) => (
                <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
              ))}
            </TextField>
            <TextField select fullWidth label="Voucher Tier" value={form.voucher_tier}
              onChange={e => setForm({ ...form, voucher_tier: e.target.value })}>
              <MenuItem value="standard">Standard (10 AZN)</MenuItem>
              <MenuItem value="premium">Premium (13 AZN)</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField fullWidth type="date" label="Date *" value={form.scheduled_date}
              onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth type="time" label="Time" value={form.scheduled_time}
              onChange={e => setForm({ ...form, scheduled_time: e.target.value })}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth type="number" label="Duration (min)" value={form.duration_minutes}
              onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 90 })}
              inputProps={{ min: 30, max: 180 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField fullWidth type="number" label="Master Class Fee (AZN)" value={form.masterclass_fee}
              onChange={e => setForm({ ...form, masterclass_fee: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 50, step: 1 }}
              helperText="This fee goes to you on top of the coffee voucher" />
            <TextField fullWidth type="number" label="Max Capacity" value={form.max_capacity}
              onChange={e => setForm({ ...form, max_capacity: parseInt(e.target.value) || 6 })}
              inputProps={{ min: 3, max: 20 }} />
          </Box>

          {/* Price preview */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: colors.neutral[50] }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Price Preview for Attendees:</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Coffee voucher ({form.voucher_tier})</Typography>
              <Typography variant="body2">{form.voucher_tier === 'premium' ? 13 : 10} AZN</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Master class fee</Typography>
              <Typography variant="body2">{form.masterclass_fee} AZN</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: `1px solid ${colors.neutral[200]}` }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Total per attendee</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {(form.voucher_tier === 'premium' ? 13 : 10) + (form.masterclass_fee || 0)} AZN
              </Typography>
            </Box>
          </Paper>

          <Alert severity="info" sx={{ mb: 2 }}>
            Your master class will be reviewed by the CoffeeMates team before it goes live. This usually takes less than 24 hours.
          </Alert>

          <Button fullWidth variant="contained" size="large" onClick={handleCreate}
            disabled={creating} sx={{ py: 1.5 }}>
            {creating ? 'Submitting...' : 'Submit for Approval'}
          </Button>
        </Paper>
      )}

      <ConfirmDialog open={confirmDlg.open} title={confirmDlg.title} message={confirmDlg.message} onConfirm={confirmDlg.onConfirm} onCancel={() => setConfirmDlg(d => ({ ...d, open: false }))} />
    </Container>
  );
}
